import { useEffect } from 'react'
import type { DataLayerAction } from '@corratech/pylot-tag-manager/src//TagContext.d'
import { getUUIDCookie } from '@corratech/pylot-tag-manager/src/TrackUserData/UserCookies'
import { useCart } from '@corratech/pylot-cart-manager'
import { useUser } from '@corratech/pylot-auth-manager'
import { useWishlist } from '@corratech/pylot-wishlist/src/Hooks/use-wishlist'
import { deepValue } from '@corratech/pylot-utils'
import { useOrders } from '@corratech/pylot-orders/src/hooks/use-orders'
import { sha256 } from 'js-sha256'

interface Product {
    price_range: {
        minimum_price: {
            regular_price: {
                value: number
            }
            final_price: {
                value: number
            }
        }
    }
}

interface Item {
    product: Product
}

export const TrackUserCustomData = ({
    dataLayerAction
}: {
    dataLayerAction: DataLayerAction
}): null => {
    const { isSignedIn, customer } = useUser()
    const { data: cartData } = useCart()
    const cart = cartData?.data?.cart
    const { wishlists } = useWishlist()
    const pageSize = 5
    const currentPage = 1
    const { orders } = useOrders(
        {
            pageSize,
            currentPage
        },
        // Skip the customer orders request for guest users — it's only used
        // when signed in (see the isSignedIn block below).
        !isSignedIn
    )

    const wishlistTotalValue = (wishlists: string | any[] | undefined) => {
        let priceTotal = 0
        if (!wishlists?.length) return priceTotal
        wishlists[0]?.items_v2?.items.forEach((item: Item) => {
            const priceRegular = deepValue(
                item.product,
                ['price_range', 'minimum_price', 'regular_price', 'value'],
                0
            )
            const priceFinal = deepValue(
                item.product,
                ['price_range', 'minimum_price', 'final_price', 'value'],
                0
            )
            const price = priceFinal > 0 ? priceFinal : priceRegular
            priceTotal += price
        })

        return priceTotal
    }

    useEffect(() => {
        if (cartData) {
            let cartTotalValue = 0
            cart?.items?.forEach(
                (item: any) => (cartTotalValue += item.quantity)
            )
            let wishlistsCount = 0
            let wishlistValue = 0
            let userAlreadyOrdered = 'no'
            const baseUrl = globalThis.location.hostname
            const customerId = customer?.customer_id

            if (isSignedIn && wishlists) {
                wishlistValue = wishlistTotalValue(wishlists)
                wishlistsCount = wishlists[0]?.items_count!
                userAlreadyOrdered = orders?.items.length ? 'yes' : 'no'
            }

            const customerObj = {
                anonymous_id: getUUIDCookie(),
                client_id: customerId ? sha256(`${baseUrl}${customerId}`) : '',
                login_status: isSignedIn ? 'Logged In' : 'Guest',
                cart_total_value: cart?.prices?.grand_total?.value,
                cart_total_count: cartTotalValue,
                user_already_ordered: userAlreadyOrdered,
                wishlist_total_value: wishlistValue,
                wishlist_total_count: wishlistsCount
            }

            if (isSignedIn && (!wishlists || !customer || !orders)) return

            dataLayerAction({
                type: 'GET_USER_DATA',
                data: {
                    ecommerce: {
                        ...customerObj
                    }
                }
            })
        }
    }, [cartData, wishlists, isSignedIn, customer, orders])

    return null
}
